<template>
  <v-container ref="container">
    <v-layout text-center wrap>
      <v-flex xs12>
        <v-toolbar-title class="headline text-uppercase text-left">
          <span class="font-weight-bold">{{ $t('Order') }} {{ order.orderName }}</span>
          <h6 class="font-weight-light">
            {{ $t('Submitted by') }} {{ order.fullName }} {{ $t('on') }}
            {{ order.dateCreated | formatDate }}
          </h6>
        </v-toolbar-title>
        <v-app>
          <v-form ref="form" class="form-types">
            <v-alert v-if="errorMessages" type="error">{{ errorMessages }}</v-alert>

            <v-row>
              <v-col text-left cols="12" md="6" v-if="preferences.fields.fullName.enabled">
                <v-text-field
                  ref="fullName"
                  color="dark"
                  outlined
                  v-model="fullName"
                  v-bind:label="$t('fullName')"
                  required
                  :rules="fullNameRules"
                ></v-text-field>
              </v-col>
              <v-col text-left cols="12" md="6" v-if="preferences.fields.phoneNumber.enabled">
                <v-text-field
                  ref="phoneNumber"
                  color="dark"
                  outlined
                  v-model="phoneNumber"
                  v-bind:label="$t('phoneNumber')"
                  :rules="phoneNumberRules"
                  required
                ></v-text-field>
              </v-col>
              <v-col text-left cols="12" md="6" v-if="preferences.fields.iban.enabled">
                <v-text-field
                  ref="iban"
                  color="dark"
                  outlined
                  v-model="iban"
                  :rules="ibanRules"
                  v-bind:label="$t('IBAN')"
                  required
                ></v-text-field>
              </v-col>
              <v-col text-left cols="12" md="6" v-if="preferences.fields.bankAccountHolder.enabled">
                <v-text-field
                  ref="bankAccountHolder"
                  color="dark"
                  outlined
                  v-model="bankAccountHolder"
                  :rules="bankAccountHolderRules"
                  v-bind:label="$t('Bank account holder (full name)')"
                  required
                ></v-text-field>
              </v-col>

              <v-col text-left cols="12" md="6" v-if="preferences.fields.address1.enabled">
                <v-text-field
                  color="dark"
                  outlined
                  v-model="address1"
                  v-bind:label="$t('Return address1')"
                  :rules="address1Rules"
                ></v-text-field>
              </v-col>

              <v-col text-left cols="12" md="6" v-if="preferences.fields.address2.enabled">
                <v-text-field
                  color="dark"
                  outlined
                  v-model="address2"
                  v-bind:label="$t('Return address2')"
                  :rules="address2Rules"
                ></v-text-field>
              </v-col>

              <v-col text-left cols="12" md="6" v-if="preferences.fields.city.enabled">
                <v-text-field
                  color="dark"
                  outlined
                  v-model="city"
                  :rules="cityRules"
                  v-bind:label="$t('City')"
                  required
                ></v-text-field>
              </v-col>

              <v-col text-left cols="12" md="6" v-if="preferences.fields.county.enabled">
                <v-text-field
                  color="dark"
                  outlined
                  v-model="county"
                  :rules="countyRules"
                  v-bind:label="$t('County')"
                  required
                ></v-text-field>
              </v-col>

              <v-col text-left cols="12" md="6" v-if="preferences.fields.zipCode.enabled">
                <v-text-field
                  color="dark"
                  outlined
                  v-model="zipCode"
                  :rules="zipCodeRules"
                  v-bind:label="$t('Zip code')"
                  required
                ></v-text-field>
              </v-col>

              <v-col text-left cols="12" md="6" v-if="preferences.fields.country.enabled">
                <v-text-field
                  color="dark"
                  outlined
                  v-model="country"
                  :rules="countryRules"
                  v-bind:label="$t('Country')"
                  required
                ></v-text-field>
              </v-col>

              <v-col text-left cols="12" md="12" v-if="preferences.fields.returnType.enabled">
                <v-select
                  color="dark"
                  class="v-select-return"
                  v-model="v_return_type_selected"
                  :items="
                    preferences.fields.returnType.options == null
                      ? v_return_type_items
                      : preferences.fields.returnType.options
                  "
                  :rules="v_return_type_items_rules"
                  v-bind:label="$t('Return transport type')"
                  item-value="id"
                  item-text="label"
                  solo
                  required
                ></v-select>
              </v-col>

              <v-col text-left cols="12" md="12" v-if="preferences.fields.description.enabled">
                <v-textarea
                  color="dark"
                  outlined
                  v-model="description"
                  v-bind:label="$t('Return comments')"
                  :rules="descriptionRules"
                ></v-textarea>
              </v-col>
            </v-row>

            <v-toolbar-title class="headline text-uppercase text-left products-text">
              <h6 class="font-weight-light mt-5">
                {{ $t('Selected products for return') }}
              </h6>
            </v-toolbar-title>

            <v-container fluid>
              <template v-for="(formField, pos) in returnProducts">
                <v-row :key="pos" class="v-row-wrap">
                  <v-col cols="12" sm="5" class="information-row">
                    <v-row v-if="formField.replaceProduct">
                      <v-col cols="12" sm="6" style="opacity: 0.4">
                        <v-img :src="formField.product.image" max-width="50" class="float-left"></v-img>
                        <v-subheader>{{ formField.product.name }}</v-subheader>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-img :src="formField.replaceProduct.image" max-width="50" class="float-left"></v-img>
                        <v-subheader
                          >{{ formField.replaceProduct.name }} - {{ formField.replaceProduct.variant }}</v-subheader
                        >
                      </v-col>
                    </v-row>
                    <div v-else class="form-image">
                      <v-img :src="formField.product.image" max-width="50" class="float-left"></v-img>
                      <v-subheader>{{ formField.product.name }}</v-subheader>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="4" class="v-select-wrap">
                    <v-select
                      v-if="preferences.fields.returnReason.enabled"
                      v-model="formField.returnReason"
                      :items="
                        preferences.fields.returnReason.options == null
                          ? v_return_reason_items
                          : preferences.fields.returnReason.options
                      "
                      :rules="v_return_reason_items_rules"
                      v-bind:label="$t('Return reason')"
                      item-value="id"
                      item-text="label"
                      required
                      solo
                      class="custom-select"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="2" align-self="center" class="change-btn-wrapper" v-if="preferences.replaceProduct">
                    <v-btn color="primary" class="btn-change" dark @click="searchProductsModalOpen(pos)">
                      <svg
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="change-svg"
                      >
                        <g clip-path="url(#clip0_407_108)">
                          <path
                            d="M13.044 9.53998V10.7466C13.044 11.1299 13.1205 11.4514 13.2716 11.7019C13.4799 12.0476 13.822 12.2457 14.2103 12.2457C14.5908 12.2457 14.969 12.0604 15.3341 11.6953L19.3195 7.70984C20.1946 6.83478 20.1946 5.41095 19.3195 4.53589L15.3341 0.550415C14.969 0.18512 14.5908 0 14.2103 0C13.6297 0 13.044 0.463623 13.044 1.49908V2.59497C7.38564 2.92914 2.70144 7.27148 2.00527 12.9567C1.96627 13.2742 2.14681 13.5782 2.44436 13.6959C2.52859 13.7292 2.61629 13.7454 2.7029 13.7454C2.92226 13.7454 3.13503 13.6426 3.27053 13.4575C5.13784 10.9061 8.13601 9.38287 11.2905 9.38287C11.875 9.38287 12.4628 9.43561 13.044 9.53998Z"
                            fill="white"
                          />
                          <path
                            d="M22.543 10.3041C22.2455 10.1862 21.906 10.2843 21.7169 10.5427C19.8497 13.0939 16.8516 14.6171 13.697 14.6171C13.1126 14.6171 12.5248 14.5644 11.9436 14.46V13.2534C11.9436 12.2179 11.3577 11.7543 10.7772 11.7543C10.3967 11.7543 10.0186 11.9396 9.65333 12.3049L5.66785 16.2902C4.79298 17.1652 4.79298 18.589 5.66804 19.4643L9.65333 23.4496C10.0186 23.8149 10.3967 24 10.7772 24C11.3577 24 11.9436 23.5364 11.9436 22.5009V21.405C17.6021 21.0709 22.2861 16.7285 22.9823 11.0435C23.0213 10.7258 22.8408 10.4218 22.543 10.3041Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_407_108">
                            <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                          </clipPath>
                        </defs>
                      </svg>

                      {{ $t('Change') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </template>

              <!--Modal Return Products-->
              <template>
                <v-row justify="center">
                  <v-dialog v-model="changeProductDialog" max-width="600px" :retain-focus="false">
                    <v-card class="dialog modal">
                      <v-card-title>
                        <div class="modal-title">
                          <span class="text-h5">{{ $t('Change product') }}</span>
                          <svg
                            @click="searchProductsModalClose"
                            width="15"
                            height="15"
                            viewBox="0 0 15 15"
                            fill="none"
                            class="close-icon"
                            xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_792_50)">
                              <path
                                d="M1.07576 13.9242C1.1193 13.9678 1.17099 14.0023 1.2279 14.0259C1.2848 14.0495 1.3458 14.0617 1.4074 14.0617C1.469 14.0617 1.53 14.0495 1.58691 14.0259C1.64381 14.0023 1.69551 13.9678 1.73904 13.9242L7.49998 8.16325L13.2633 13.9242C13.3512 14.0121 13.4705 14.0616 13.5949 14.0616C13.7193 14.0616 13.8386 14.0121 13.9265 13.9242C14.0145 13.8362 14.0639 13.7169 14.0639 13.5925C14.0639 13.4682 14.0145 13.3489 13.9265 13.2609L8.16326 7.49997L13.9242 1.73669C14.0122 1.64873 14.0616 1.52944 14.0616 1.40505C14.0616 1.28066 14.0122 1.16136 13.9242 1.07341C13.8362 0.985449 13.7169 0.936035 13.5926 0.936035C13.4682 0.936035 13.3489 0.985449 13.2609 1.07341L7.49998 6.83669L1.7367 1.07575C1.64703 0.998955 1.53168 0.958828 1.41371 0.963384C1.29573 0.967941 1.18382 1.01685 1.10034 1.10033C1.01686 1.18381 0.967954 1.29572 0.963398 1.41369C0.958841 1.53167 0.998969 1.64701 1.07576 1.73669L6.8367 7.49997L1.07576 13.2632C0.988457 13.3511 0.939453 13.4699 0.939453 13.5937C0.939453 13.7176 0.988457 13.8364 1.07576 13.9242Z"
                                fill="black"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_792_50">
                                <rect width="15" height="15" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </v-card-title>
                      <v-card-text>
                        <div class="products-search">
                          <v-text-field
                            class="products-search-input"
                            color="dark"
                            outlined
                            v-model="search"
                            :label="$t('Search products')"
                            @keyup="getProductsByName"
                          >
                            <template v-slot:append>
                              <v-img style="margin: auto 0" height="14" width="14" src="../assets/search.svg" />
                            </template>
                          </v-text-field>
                        </div>

                        <div class="products-list" :style="`max-height: ${windowHeight / 2}px;`">
                          <v-row v-if="allProductsLoading" align-content="center" justify="center" class="getting">
                            <v-col class="text-subtitle-1 text-center" cols="12"> Getting your products </v-col>
                            <v-col cols="6">
                              <v-progress-linear
                                color="progress-linear "
                                indeterminate
                                rounded
                                height="6"
                              ></v-progress-linear>
                            </v-col>
                          </v-row>

                          <div v-if="allProducts && allProducts.length && !allProductsLoading">
                            <v-radio-group v-model="changeProductVariant" style="margin-top: 0">
                              <div v-for="product in allProducts" :key="product.name" class="product-wrapper">
                                <div class="product" @click="e => expandProduct(e, product.name)">
                                  <div class="image float-left">
                                    <v-img :src="product.options.image" max-width="50" class="product-img"></v-img>
                                  </div>

                                  <div class="name">
                                    {{ product.name }}
                                  </div>
                                  <div class="expand">
                                    <!-- <span class="expand__text" v-if="true" @click="e => expandAllChilds(e)"
                                      >Expand all</span -->

                                    <span
                                      class="expand__arrow"
                                      v-bind:class="{
                                        expandedBlock: expandedId === product.name,
                                      }"
                                    >
                                      <svg
                                        width="16"
                                        height="8"
                                        class="modal-expand-arrow"
                                        viewBox="0 0 16 8"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M2.02712 7.82286L8.48662 1.5262L14.9461 7.82285C15.0615 7.93559 15.2165 7.99871 15.3778 7.99871C15.5391 7.99871 15.6941 7.93559 15.8095 7.82285C15.8653 7.7681 15.9097 7.70275 15.94 7.63063C15.9704 7.5585 15.986 7.48105 15.986 7.40282C15.986 7.32458 15.9704 7.24714 15.94 7.17501C15.9097 7.10289 15.8653 7.03754 15.8095 6.98278L8.93768 0.282891C8.817 0.165263 8.65514 0.0994302 8.48662 0.0994303C8.3181 0.0994303 8.15625 0.165263 8.03557 0.282891L1.16378 6.98149C1.10751 7.03629 1.06278 7.1018 1.03224 7.17416C1.0017 7.24653 0.985962 7.32427 0.985962 7.40282C0.985962 7.48137 1.0017 7.55911 1.03224 7.63148C1.06278 7.70384 1.10751 7.76936 1.16378 7.82415C1.27919 7.93689 1.43412 8 1.59545 8C1.75679 8 1.91172 7.93689 2.02712 7.82415L2.02712 7.82286Z"
                                          fill="black"
                                        />
                                      </svg>
                                    </span>
                                  </div>
                                </div>
                                <div
                                  class="product-variant-items"
                                  v-bind:class="{ 'one-row': hasExpand(product) }"
                                  v-if="expandedId === product.name"
                                >
                                  <div
                                    v-for="variant in product.options.variants"
                                    class="product-variant"
                                    :key="variant.variant"
                                  >
                                    <v-radio :value="variant" class="radio"> </v-radio>
                                    <div class="product-variant-details">
                                      <div class="float-left">
                                        {{ variant.variant }}
                                      </div>
                                      <div class="float-right">
                                        <span class="float-price"> {{ variant.price }} </span
                                        ><span class="float-currency">{{ variant.currency }}</span>
                                        <v-icon></v-icon>
                                      </div>
                                    </div>
                                    <div class="expand__arrow" v-if="variant.variant.length > 10">
                                      <v-img width="10" height="6" src="../assets/arrow-variant-expand.svg" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </v-radio-group>
                          </div>
                          <div v-else>
                            <v-alert dense outlined type="info" v-if="!allProductsLoading">
                              <span class="title">{{ $t('No products, search for products') }}</span>
                              <span class="subtitle"
                                >{{ $t('Enter a search query to find the product you are interested in') }}</span
                              >
                            </v-alert>
                          </div>
                        </div>
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="mb-3 mb-sm-0 mr-0 mr-sm-5 btn-anulat" @click="changeProductDialog = false">
                          {{ $t('Cancel') }}
                        </v-btn>
                        <v-btn color="mb-3 mb-sm-0 mr-0 mr-sm-5 btn-return" @click="changeProduct">
                          {{ $t('Save') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-row>
              </template>
              <!--End Modal Return Products-->
            </v-container>

            <v-flex xs12 style="margin: 35px 0 13px">
              <v-checkbox v-model="agreeTerms" class="v-slot--term">
                <template v-slot:label>
                  <div>
                    {{ $t('I agree') }}
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <a target="_blank" :href="preferences.termsLink" @click.stop v-on="on">
                          {{ $t('T&C') }}
                        </a>
                      </template>
                      {{ $t('Open') }}
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
            </v-flex>
            <v-flex xs12>
              <v-btn color="error" class="v-btn--back" @click="goBack">{{ $t('Cancel and go back') }}</v-btn>
              <v-btn
                class="mx-5 my-3 my-sm-0 v-btn--return"
                @click="proceed"
                color="dark"
                :dark="agreeTerms"
                :disabled="!agreeTerms"
                >{{ $t('Submit return') }}</v-btn
              >
            </v-flex>
          </v-form>
        </v-app>
      </v-flex>
    </v-layout>
    <div v-if="themeJson.name === 'custom'">
      <div v-html="styles"></div>
    </div>
    <v-overlay :value="overlay" :color="'white'">
      <v-progress-circular indeterminate size="64" :color="'black'"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import Vue from 'vue';
import { validationMixin } from 'vuelidate';
import /*required, maxLength, email*/ 'vuelidate/lib/validators';
import { mapGetters, mapActions, mapState } from 'vuex';
import moment from 'moment';
import LoadScript from 'vue-plugin-load-script';

Vue.use(LoadScript);

Vue.filter('formatDate', function (value) {
  if (value) {
    return moment(String(value)).format('DD/MM/YYYY HH:mm');
  }
});

Vue.loadScript('/iban.js');

export default {
  mixins: [validationMixin],
  computed: {
    ...mapGetters(['order', 'selectedProducts', 'preferences', 'allProducts', 'allProductsLoading']),
    ...mapState(['themeJson']),

    form() {
      return {
        iban: this.iban,
        bankAccountHolder: this.bankAccountHolder,
      };
    },
  },

  data: vm => ({
    windowHeight: 300,
    search: '',
    styles: '',
    changeProductVariant: null,
    allProductsHeaders: [
      { text: 'Name', align: 'left', value: 'name' },
      { text: '', value: '-' },
      { text: 'Image', value: '-' },
      { text: 'Variant', value: 'variant' },
      { text: 'Price', value: 'price' },
    ],
    agreeTerms: false,
    expanded: [],
    singleExpand: false,
    formHasErrors: false,
    errorMessages: null,
    overlay: false,
    singleSelect: false,
    changeProductDialog: false,
    changeProductDialogId: null,
    expandedId: null,
    selected: [],
    fullName: vm.$store.state.all.fullName,
    fullNameRules: [v => (vm.preferences.fields.fullName.required ? !!v || vm.$t('Full name is required') : true)],
    phoneNumber: vm.$store.state.all.phoneNumber,
    phoneNumberRules: [
      v => (vm.preferences.fields.phoneNumber.required ? !!v || vm.$t('Phone number is required') : true),
    ],
    address1: vm.$store.state.all.address1,
    address1Rules: [v => (vm.preferences.fields.address1.required ? !!v || vm.$t('Address1 is required') : true)],
    address2: vm.$store.state.all.address2,
    address2Rules: [v => (vm.preferences.fields.address2.required ? !!v || vm.$t('Address2 is required') : true)],
    city: vm.$store.state.all.city,
    cityRules: [v => (vm.preferences.fields.city.required ? !!v || vm.$t('City is required') : true)],
    county: vm.$store.state.all.county,
    countyRules: [v => (vm.preferences.fields.county.required ? !!v || vm.$t('County is required') : true)],
    zipCode: vm.$store.state.all.zipCode,
    zipCodeRules: [v => (vm.preferences.fields.zipCode.required ? !!v || vm.$t('Zip code is required') : true)],
    country: vm.$store.state.all.country,
    countryRules: [v => (vm.preferences.fields.country.required ? !!v || vm.$t('Country is required') : true)],
    description: null,
    descriptionRules: [
      v => (vm.preferences.fields.description.required ? !!v || vm.$t('Description is required') : true),
    ],

    iban: null,
    ibanRules: [
      v => {
        if (vm.preferences.fields.iban.required) {
          return !!v || vm.$t('IBAN is required');
        }
        return true;
      },
      v => {
        if (!v) return true;
        return IBAN.isValid(v) || vm.$t('IBAN is invalid');
      }
    ],

    bankAccountHolder: null,
    bankAccountHolderRules: [
      v => (vm.preferences.fields.bankAccountHolder.required ? !!v || vm.$t('Bankaccount holder is required') : true),
    ],

    v_return_type_items: [
      { id: 'COURIER', label: vm.$t('RETURN_TYPE_COURIER') },
      { id: 'DROP', label: vm.$t('RETURN_TYPE_DROP') },
    ],
    v_return_type_items_rules: [
      v => (vm.preferences.fields.returnType.required ? !!v || vm.$t('Return transport type is required') : true),
    ],
    v_return_type_selected: '',

    v_return_reason_items: [
      {
        id: 'NOT_NEEDED_ANYMORE',
        label: vm.$t('RETURN_REASON_NOT_NEEDED_ANYMORE'),
      },
      { id: 'WRONG_SIZE', label: vm.$t('RETURN_REASON_WRONG_SIZE') },
      { id: 'WRONG_COLOR', label: vm.$t('RETURN_REASON_WRONG_COLOR') },
      { id: 'DEFECTIVE', label: vm.$t('RETURN_REASON_DEFECTIVE') },
    ],
    v_return_reason_items_rules: [
      v => (vm.preferences.fields.returnReason.required ? !!v || vm.$t('Return reason is required') : true),
    ],

    returnProducts: [],

    headers: [
      { text: vm.$t('Product name'), value: 'name' },
      { text: vm.$t('Quantity'), value: 'quantity' },
      { text: vm.$t('Color'), value: 'variant' },
    ],
  }),
  validations: {},
  watch: {
    overlay(val) {
      val &&
        setTimeout(() => {
          this.overlay = false;
        }, 1000);
    },
  },
  created() {
    this.styles = this.getStyles();
  },
  mounted() {
    this.windowHeight = this.$refs.container.scrollHeight;
    this.overlay = true;
    this.verifyOrder();
    //this.getAllProducts();
    let returnProducts = this.returnProducts;

    this.selectedProducts.forEach(function (product, index) {
      // eslint-disable-next-line no-console
      // console.log( product.name + " -> " + product.quantity );
      for (index = 0; index < product.quantity; index++) {
        returnProducts.push({
          product: {
            name: product.name,
            variant: product.variant,
            sku: product.sku,
            image: product.image,
          },
          productId: product.productId,
          variantId: product.variantId,
          lineItemId: product.lineItemId,
          returnReason: '',
        });
      }
    });
  },
  methods: {
    verifyOrder() {
      if (this.selectedProducts.length == 0) {
        this.$router.push({
          path: '/',
          query: { shop: this.$store.state.shop, lang: this.$i18n.locale },
        });
      } else {
        return;
      }
    },
    getStyles() {
      const theme = this.themeJson;
      return `<style> 
      
body.theme-custom .v-application {
    background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .container .v-toolbar__title {
      color: ${theme.textColor} !important;
}

body.theme-custom .v-application .error--text {
      color: #ff5252 !important;
}

body.theme-custom .dialog .v-card__title {
      color: ${theme.textColor} !important;
}

body.theme-custom .dialog .product-wrapper {
      background-color: ${theme.inputBackgroundColor} !important;
}

body.theme-custom .dialog .product-wrapper .product {
      color: ${theme.textColor} !important;
}

body.theme-custom .dialog .product-wrapper .product-variant .product-variant-details {
      color: ${theme.textColor} !important;
}

body.theme-custom .dialog .v-input--is-focused fieldset {
      border: 1px solid ${theme.selectBorderColor} !important;
}

body.theme-custom .dialog .btn-anulat {
      background-color: ${theme.cancelBtnColor} !important;
      color: ${theme.btnTextColor} !important;
}

body.theme-custom .dialog .btn-return {
      background-color: ${theme.sendBtnColor} !important;
      color: ${theme.btnTextColor} !important;
}

body.theme-custom .dialog .product-wrapper .product-variant .product-variant-details .float-price {
      box-shadow: unset !important;
}

body.theme-custom .change-svg path{  
    fill: ${theme.btnTextColor} !important;
}

body.theme-custom .dialog .product-wrapper .product-variant {
      background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .v-label {
      color: ${theme.textColor} !important;
}

body.theme-custom .dialog .v-sheet.v-alert {
      background-color: ${theme.inputBackgroundColor} !important;
      color: ${theme.textColor} !important;
}

body.theme-custom .container--fluid .v-subheader {
      color: ${theme.textColor} !important;
}

body.theme-custom .close-icon path{
    fill: ${theme.textColor};
    
}

body.theme-custom .custom-select .v-input__control .v-input__slot .v-select__slot .v-label{
      color: #000 !important;
}

body.theme-custom .v-input input {
      color: ${theme.inputTextColor} !important;
}

body.theme-custom .container .v-application--is-ltr fieldset {
    background-color: ${theme.inputBackgroundColor} !important;
}

body.theme-custom .products-search-input .v-input__slot {
      border: ${theme.inputBackgroundColor === '#ffffff' ? '1px solid #c9cccf !important' : 'unset !important'};
}

body.theme-custom .v-select__selection--comma {
      color: ${theme.textColor};
}

body.theme-custom .dialog .v-sheet.v-alert .mdi:before {
      color: ${theme.textColor} !important;
}

body.theme-custom .container .v-btn--back {
      background-color: ${theme.cancelBtnColor} !important;
      color: ${theme.btnTextColor} !important;
}

body.theme-custom .container .v-btn.v-btn--disabled.v-btn--has-bg {
      color: ${theme.btnTextColor} !important;
}

body.theme-custom .container .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
      background-color: ${theme.sendBtnColor} !important;
       color: ${theme.btnTextColor} !important;
}

body.theme-custom .dialog .products-search .v-input__slot fieldset legend {
      width: 0 !important;
}

body.theme-custom .container .v-application--wrap .custom-select .v-select__slot .mdi:before {
      color: rgba(0, 0, 0, 0) !important;
}

body.theme-custom .container .v-application--wrap .v-slot--term .v-input__slot .v-label a {
      color: ${theme.textColor} !important;
}

body.theme-custom .custom-select .v-select__selection--comma {
      color: #000 !important;
}

body.theme-custom .container .v-application--wrap .v-select__slot .mdi:before {
      color: ${theme.textColor} !important;
}

body.theme-custom .v-input--checkbox .v-icon {
      background-color: ${theme.textColor} !important;
}

body.theme-custom .container .v-application--wrap .v-input .v-input__slot {
      border-width: ${theme.inputBackgroundColor === '#ffffff' ? '1px' : '0 !important'}
}

body.theme-custom .v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot textarea {
      color: ${theme.textColor} !important;
}

body.theme-custom .container .v-application--is-ltr .v-text-field.v-input--is-focused fieldset {
      border: 1px solid ${theme.selectBorderColor} !important;
}

body.theme-custom .container--fluid .btn-change {
      background-color: ${theme.changeBtnColor} !important;
      color: ${theme.btnTextColor} !important;
}

body.theme-custom .dialog {
    color: white !important;
    background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .dialog fieldset {
    background: ${theme.inputBackgroundColor} !important;
}

body.theme-custom .dialog .products-search {
    background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: ${theme.inputBackgroundColor} !important;
}

body.theme-custom .container--fluid .row:not(:last-child) {
    background-color: ${theme.inputBackgroundColor} !important;
}

body.theme-custom .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: ${theme.textColor};
}

body.theme-custom .products-list .text-subtitle-1 {
      color: ${theme.textColor};
}

body.theme-custom .progress-linear {
      background-color: ${theme.textColor} !important;
      border-color: ${theme.textColor} !important;
}

body.theme-custom .modal-expand-arrow path {
      fill: ${theme.textColor} !important;
}

}</style> `;
    },
    ...mapActions([
      'send_products_back',
      'setIban',
      'setFullName',
      'setPhoneNumber',
      'setAddress1',
      'setAddress2',
      'setCity',
      'setCounty',
      'setZipCode',
      'setCountry',
      'setBankAccountHolder',
      'setReturnType',
      'setDescription',
      'setReturnProducts',
      'getAllProducts',
      'setAllProducts',
      'searchProducts',
    ]),

    goBack: function () {
      this.$router.push('/order-products');
    },

    proceed: function () {
      let valid = this.$refs.form.validate();
      if (valid) {
        this.setIban(this.iban);
        this.setFullName(this.fullName);
        this.setPhoneNumber(this.phoneNumber);
        this.setAddress1(this.address1);
        this.setAddress2(this.address2);
        this.setCity(this.city);
        this.setCounty(this.county);
        this.setZipCode(this.zipCode);
        this.setCountry(this.country);
        this.setBankAccountHolder(this.bankAccountHolder);
        this.setReturnType(this.v_return_type_selected);
        this.setDescription(this.description);
        this.setReturnProducts(this.returnProducts);

        this.overlay = true;
        this.send_products_back()
          .then(() => {
            this.$router.push('/success-return');
          })
          .catch( error => {
            if (!navigator.onLine) {
              this.errorMessages = this.$t('No network connection');
            } else {
              if (error.response && error.response.data && error.response.data.message) {
                this.errorMessages = this.$t(error.response.data.message);
              } else {
                this.errorMessages = this.$t("Ooops something's wrong");
              }
            }
          });
      }
    },

    changeProduct: function () {
      let productKey = this.changeProductDialogId;
      this.returnProducts[productKey].replaceProductId = this.changeProductVariant.productId;
      this.returnProducts[productKey].replaceVariantId = this.changeProductVariant.variantId;
      this.returnProducts[productKey].replaceProduct = {
        name: this.changeProductVariant.name,
        variant: this.changeProductVariant.variant,
        image: this.changeProductVariant.image,
      };
      this.changeProductDialog = false;
    },
    expandProduct: function (e, productName) {
      if (!e.target.classList.contains('expand__text')) {
        if (this.expandedId !== productName) this.expandedId = productName;
        else this.expandedId = null;
      }
    },
    expandProductVariant: function (e) {
      e.target.closest('.product-variant').classList.toggle('expanded');
    },
    expandAllChilds: function (e) {
      e.target.closest('.product-wrapper') &&
        [...e.target.closest('.product-wrapper').getElementsByClassName('product-variant')].forEach(
          elem => !elem.classList.contains('expanded') && elem.classList.add('expanded')
        );
    },

    getProductsByName: function () {
      if (this.search.length > 2) {
        this.searchProducts(this.search);
      }
    },

    hasExpand: function (product) {
      if (product.options.variants.filter(variant => variant.variant.length > 25).length > 0) {
        return true;
      } else return false;
    },

    searchProductsModalOpen(productKey) {
      this.changeProductDialog = true;
      this.changeProductDialogId = productKey;
      this.search = '';
      this.setAllProducts([]);
    },
    searchProductsModalClose() {
      this.changeProductDialog = false;
      this.changeProductDialogId = null;
      this.search = '';
      this.setAllProducts([]);
    },
  },
};
</script>

<style lang="scss">
.modal-title {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.close-icon {
  cursor: pointer;
  width: 15px;
  height: 15px;
  path {
    fill: #000;
  }
}

@media screen and (max-width: 768px) {
  .v-dialog {
    margin: 12px;
    .dialog {
      .v-card__title {
        padding: 10px 10px 20px 10px !important;
        .text-h5 {
          font-size: 16px !important;
          line-height: 19px !important;
        }
      }
      .products-search {
        padding: 0 10px !important;
      }
      .v-sheet.v-alert {
        padding: 20px;
        margin: 10px;
      }
      .v-card__actions {
        padding: 10px !important;
        & > .v-btn.v-btn {
          height: 40px;
        }
      }
      .v-card__text {
        padding: 0 24px !important;

        .v-text-field__slot input {
          font-size: 14px;
        }
      }
      .product-wrapper {
        margin: 0 2px 0 10px;

        .product {
          .v-image {
            max-width: 35px !important;
            min-width: 35px !important;
          }
          .name {
            font-size: 12px;
            line-height: 14px;
          }
          .v-responsive__content {
            width: 12px !important;
          }
          .expand {
            margin-right: 5px;
            min-width: max-content;

            .expand__text {
              font-size: 12px;
              line-height: 14px;
              margin-left: 10px;
            }
            .expand__arrow {
              .v-image__image {
                background-size: 12px 6px;
              }
              .v-image {
                max-width: 12px !important;
                min-width: 12px !important;
              }
            }
          }
        }
        .product-variant .v-input--selection-controls__input {
          max-height: 16px;
          max-width: 16px;

          .v-icon {
            font-size: 19px;
          }
        }
      }
      .v-input--radio-group__input {
        gap: 7px;
      }
    }
  }

  .form-types {
    .row {
      row-gap: 0 !important;
      .v-text-field.error--text.v-input {
        margin-bottom: 10px;
        .v-input__control .v-input__slot {
          border: 1px solid #ff5252 !important;
          fieldset {
            border: none !important;
          }
        }
      }
      .v-text-field__details {
        bottom: -28px !important;
      }
      .v-input__slot {
        min-height: 40px;
        font-size: 14px;
        .v-label {
          font-size: 14px;
        }
      }
      .v-text-field.v-text-field--solo .v-input__control {
        max-height: 40px;
        .v-label {
          font-size: 14px;
        }
      }
      .v-textarea {
        .v-label {
          font-size: 14px;
        }
      }
      .v-text-field--outlined .v-label {
        top: 11px;
      }

      .v-text-field.v-input--is-focused .v-label {
        top: 3px !important;
      }
    }
    .container--fluid .btn-change {
      min-width: auto !important;
      max-width: max-content !important;
    }
  }
}

.dialog {
  .products-list {
    .v-input__slot {
      border-width: 0 !important;
    }
  }
}

.products-list {
  max-height: 60vh !important;
  .product-variant-items {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row !important;
    gap: 8px;
  }
  .product-variant {
    width: calc(50% - 4px);
  }
}
.products-search {
  padding: 0 24px;
  margin: 0 -24px 0;
  background-color: #fff;
  .v-input__append-inner {
    margin: auto;
  }
}
.products-search-input {
  .v-label {
    top: 9px;
    color: #a6aaba !important;
    &.v-label--active {
      top: 0 !important;
      left: -15px !important;
    }
  }
  .v-input__slot {
    min-height: 40px !important;
    padding: 0 13px 0 19px !important;
    border-radius: 6px;
    border: 1px solid #c9cccf;
    fieldset {
      border: none !important;
    }
    .v-image__image--cover {
      filter: none !important;
    }
  }
}

.products-list {
  & {
    margin-left: -24px;
    margin-right: -24px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .product {
    cursor: pointer;
    & {
      border-bottom: 1px solid #c9cccf;
      padding: 10px 24px;
      display: flex;
      align-items: center;
    }

    .image {
      margin-right: 10px;
    }
  }

  .product-variant {
    & {
      border-bottom: 1px solid #c9cccf;
      padding: 10px 25px 10px 45px;
    }

    &:hover {
      background-color: #ececec;
    }

    .product-variant-details {
      width: 100%;
    }
    &-items.one-row {
      display: flex;
      flex-direction: column !important;
      gap: 8px;
      .product-variant {
        width: 100%;
      }
    }
  }
}

body.modal-open {
  overflow: hidden;
}

.dialog {
  background-color: #fff !important;
  .v-card__text {
    padding: 0 24px !important;
  }
  .v-sheet.v-alert {
    margin: 24px;
    background-color: #f7f7f7 !important;
    border-radius: 5px !important;
    border: none !important;
    padding: 20px;
    color: black !important;
    .v-alert__content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      .title {
        font-size: 16px !important;
        line-height: 18px;
        font-weight: 400;
      }
      .subtitle {
        font-size: 12px;
        line-height: 14px;
        font-weight: 300;
      }
    }
    .mdi:before {
      color: #39a0ff !important;
    }
  }
  .v-card__title {
    padding: 24px !important;
  }
  .v-text-field__details {
    display: none;
  }
  .v-input--radio-group__input {
    gap: 8px;
  }
  .product-wrapper {
    margin: 0 16px 0 25px;
    padding: 8px;
    border-radius: 5px;
    background-color: #f7f7f7;
    gap: 8px;
    display: flex;
    flex-direction: column;
    .product {
      border-bottom: none;
      padding: 0;
      .v-image {
        border-radius: 5px;
      }
      .name {
        font-size: 14px;
        line-height: 1.7rem;
      }
      .expand {
        margin-left: auto;
        display: flex;
        align-items: center;
        margin-right: 15px;
        .expand__text {
          font-size: 14px;
          text-decoration: underline;
        }
        .expand__arrow {
          margin-left: 26px;
          transform: rotateX(180deg);
          &.expandedBlock {
            transform: none;
          }
        }
      }
    }
    .product-variant {
      border-bottom: none;
      background-color: white;
      border-radius: 5px;
      height: 30px;
      padding: 5px;
      font-size: 12px;
      line-height: 14px;
      display: flex;
      align-items: center;
      cursor: pointer;

      .v-input .v-label,
      .product-variant-details {
        font-size: 12px;
        line-height: 20px;
        color: black;
        display: flex;
        align-items: center;
        gap: 8px;
        justify-content: space-between;
        width: calc(100% - 48px);
        .float-left {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: clip;
          max-width: 145px;
          margin-right: -3px;
        }
        .float-price {
          font-weight: 600;
          box-shadow: -19px 0px 5px -2px #fff;
        }
        .float-currency {
          font-weight: 300;
        }
        .float-right {
          min-width: max-content;
        }
      }
      .v-input--selection-controls__input {
        max-height: 20px;
        max-width: 20px;
        input {
          display: none;
        }
      }
      .v-input--selection-controls__ripple {
        display: none;
      }

      .expand__arrow {
        margin-left: 6px;
      }
      .radio {
        margin-bottom: 0 !important;
      }

      &.expanded {
        height: auto;

        .expand__arrow {
          transform: rotateX(180deg);
        }
        .float-left {
          white-space: inherit;
          overflow: auto;
          text-overflow: none;
          max-width: 140px;
        }
      }
    }
  }
  .v-card__actions {
    display: flex;
    padding: 24px !important;
    align-items: center;
    justify-content: flex-end;
    gap: 17px;
  }
  .btn-anulat {
    background: #0d2149 !important;
    color: #fff;
    margin: 0 !important;
  }
  .btn-return.v-btn--disabled {
    background: #b2d7f9 !important;
    color: #fff !important;
  }
  .btn-return {
    background: #39a0ff !important;
    color: #fff !important;
    margin: 0 !important;
  }
}

.v-application {
  .v-text-field__details {
    margin: 10px 0;
    padding: 0;
  }

  .primary--text {
    color: black !important;
    caret-color: black !important;
  }
}
.v-dialog {
  overflow-y: hidden;
}
.products-list .text-subtitle-1 {
  color: black;
}
.progress-linear {
  background-color: #39a0ff !important;
  border-color: #39a0ff !important;
}
.getting {
  margin: 30px !important;
}
@media screen and (max-width: 950px) {
  .information-row {
    min-width: 100% !important;
    max-width: 100%;
  }
}
.container {
  padding: 12px;

  .v-text-field.v-text-field--solo .v-input__control {
    min-height: 40px;
  }
  .v-application--wrap {
    .v-text-field--outlined {
      & legend {
        width: 0 !important;
      }
    }
    .v-select-wrap {
      .v-input__slot {
        height: 40px;
        border-color: transparent !important;
      }
      .v-text-field.error--text {
        margin-bottom: 0 !important;
      }
    }

    .v-select__slot {
      .mdi:before {
        color: rgb(0 0 0 / 0%);
      }
    }
    .v-slot--term {
      .v-input__slot {
        display: flex;
        justify-content: center;
        .v-label {
          color: #000;
          padding-left: 18px;
          a {
            color: #000;
          }
        }
      }
      .v-label {
        flex: inherit;
      }
    }
  }
  .v-input__append-inner {
    .v-input__icon--append {
      background-image: url(../assets/arrow-select.svg);
      background-position: center;
    }
  }
  .v-application--is-ltr {
    fieldset {
      border-radius: 6px;
      border-color: transparent !important;
      background-color: #fff;
    }
    .v-text-field.v-input--is-label-active,
    .v-text-field.v-input--is-focused {
      .v-label {
        left: 0 !important;
        top: 4px !important;
      }
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: 1px solid #d7d7d7 !important;
      }
    }
  }
  .v-text-field__details {
    position: absolute;
    bottom: -35px;
  }
  .form-image {
    display: flex;
    align-items: center;
    margin-top: 3px;
  }
  &--fluid {
    padding: 20px 0 0 0;
    .row:not(:last-child) {
      border-radius: 6px;
      background-color: #f3f3f3;
      margin: 0;
      padding: 10px 0;
    }
    .v-subheader {
      color: #000;
    }

    .change-btn-wrapper {
      max-width: max-content;
      @media screen and (max-width: 950px) {
        min-width: auto !important;
        max-width: max-content;
        padding: 5px;
      }
    }
    .v-select-wrap {
      @media screen and (max-width: 950px) {
        min-width: calc(100% - 130px);
        max-width: calc(100% - 127px) !important;
        padding: 5px;
        .v-text-field.error--text {
          margin-bottom: 0 !important;
          .v-text-field__details {
            min-width: max-content;
            bottom: -33px !important;
          }
        }
      }
    }

    .btn-change {
      position: relative;
      /* right: 35px; */
      background-color: #e98519 !important;
      width: -webkit-fill-available;
      min-width: 197px !important;

      @media screen and (max-width: 768px) and (min-width: 630px) {
        & {
          /* min-width: 107px !important; */
        }
      }
      @media screen and (max-width: 950px) and (min-width: 769px) {
        & {
          min-width: 100% !important;
        }
      }
      .v-icon--left {
        width: 22px !important;
        height: 22px !important;
        &::before {
          background-image: url(../assets/i-change-btn.svg);
          width: 22px !important;
          height: 22px !important;
          display: block;
          background-size: cover;
          content: '';
        }
      }
    }
  }
  .v-textarea {
    & textarea {
      resize: none;
    }
  }
  .v-row-wrap {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    padding: 15px !important;
    & > div {
      display: flex;
      align-items: center;
    }
  }
  .v-select-wrap {
    /* margin-right: -40px; */
  }

  .v-input--selection-controls__input {
    .mdi:before {
      color: #fff0;
      border: 1px solid #7d7c7c;
      border-radius: 5px;
      margin: 0;
      padding: 0;
    }
    .mdi.mdi-checkbox-marked {
      background-image: url(../assets/checkbox-checked.svg);
      background-size: cover;
    }
  }
  .v-btn {
    &--back {
      background-color: #0d2149 !important;
    }
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background-color: #39a0ff !important;
    color: #fff;
  }
  .v-select-return {
    padding-bottom: 10px;
    .v-input__append-inner {
      display: none;
    }
  }
}

.change-svg {
  margin-right: 0.5rem;
  path {
    fill: #fff !important;
  }
}

.container {
  .v-application--wrap {
    .v-input {
      .v-input__slot {
        border-width: 1px !important;
        border-style: solid !important;
        border-color: #c9cccf !important;
      }
    }
  }
}

.container {
  .v-application--wrap {
    .v-slot--term {
      .v-input__control {
        .v-input__slot {
          border-width: none !important;
          border-style: none !important;
          border-color: none !important;
        }
      }
    }
  }
}

.container {
  .v-application--wrap {
    .v-input.v-input--is-focused {
      .v-input__slot {
        border: 1px solid #000 !important;
      }
    }
  }
}

//themes
.theme-white {
  .container {
    .v-application--wrap {
      .v-input.v-input--is-focused {
        .v-input__slot {
          border: 1px solid #000 !important;
        }
      }
    }
  }

  .change-svg {
    path {
      fill: #fff !important;
    }
  }
  .v-application {
    background-color: #fff;
  }
  .container {
    .v-application--wrap {
      .v-input {
        .v-input__slot {
          border-width: 1px !important;
          border-style: solid !important;
          border-color: #c9cccf !important;
        }
      }
    }
  }
  .container {
    .v-application--wrap {
      .v-input--checkbox {
        .v-input__slot {
          border: none !important;
        }
      }
    }
  }
}
.container--fluid {
  display: flex;
  flex-direction: column;
}

.theme-blue {
  .container {
    .v-application--wrap {
      .v-input.v-input--is-focused {
        .v-input__slot {
          border: 1px solid #f7d523 !important;
        }
      }
    }
  }
  .change-svg {
    path {
      fill: #000 !important;
    }
  }
  .container {
    .v-application--wrap {
      .v-input {
        .v-input__slot {
          border-width: none !important;
          border-style: none !important;
          border-color: none !important;
        }
      }
    }
  }
  .products-list .text-subtitle-1 {
    color: white;
  }
  .progress-linear {
    background-color: #f7d523 !important;
    border-color: #f7d523 !important;
  }
  .btn-anulat {
    background: #1d64bf !important;
    color: #fff !important;
  }
  .btn-return.v-btn--disabled {
    background: #2b524c !important;
    color: #5f6683 !important;
  }
  .btn-return {
    background: #4fbc47 !important;
    color: #fff !important;
  }
  .btn-change {
    background: #f7d523 !important;
    color: #1b254e !important;
  }
  .dialog {
    color: white !important;
    background-color: #1b254e !important;

    .product-variant {
      background-color: #1b254e !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #1b254e !important;
    }
    .products-search {
      background-color: #1b254e;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }
    .v-image__image.v-image__image--cover {
      filter: invert(1);
    }
    .product-img .v-image__image.v-image__image--cover {
      filter: none;
    }
    .v-card,
    .v-input,
    .product-wrapper .product-variant .product-variant-details {
      color: white !important;
    }
    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #2a335b !important;
      color: white !important;

      .v-application .info--text,
      .v-icon.v-icon {
        color: #fff !important;
        caret-color: #fff !important;
      }
    }
  }

  .v-application--is-ltr {
    fieldset {
      background: #2a335b;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: none !important;
      }
    }
    .v-text-field .v-label {
      color: #fff;
    }
  }

  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #2a335b !important;
  }
  .container--fluid {
    display: flex;
    flex-direction: column;
    .row:not(:last-child) {
      background-color: #2a335b;
      margin-top: 20px;
    }
    .v-subheader {
      color: #fff;
    }
  }
  .v-select-wrap {
    .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
      background: #4c5475 !important;
    }
  }
  .mdi:before {
    color: #fff0;
  }
  .v-input__append-inner .v-input__icon--append,
  .v-input--selection-controls__input .mdi.mdi-checkbox-marked {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
  .container--fluid .btn-change .v-icon--left::before {
    filter: brightness(0) saturate(100%) invert(15%) sepia(33%) saturate(1415%) hue-rotate(192deg) brightness(86%)
      contrast(96%);
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #fff !important;
    & a {
      color: #fff !important;
    }
  }
  .v-btn.v-btn--back {
    background: #1d64bf !important;
    color: #fff !important;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #4fbc47 !important;
  }
}

.theme-light-green {
  .v-application .v-application--is-ltr .v-text-field.v-input--is-focused .v-input__slot {
    border: 1px solid #fff !important;
    &::after,
    &::before {
      display: none;
    }
  }
  .v-application .v-application--is-ltr .v-text-field.v-input--is-focused .v-label {
    color: #8fc026;
  }
  .second-tab .v-text-field.v-input--is-focused .v-label {
    top: 0px !important;
    left: 0px !important;
  }
  .products-list .text-subtitle-1 {
    color: black;
  }
  .dialog {
    .v-sheet.v-alert {
      .mdi-information::before {
        color: #8fc026 !important;
      }
    }
  }

  .progress-linear {
    background-color: #8fc026 !important;
    border-color: #8fc026 !important;
  }
  .btn-anulat {
    background: #fff !important;
    color: #000;
  }
  .btn-return.v-btn--disabled {
    background: #97c497 !important;
    color: #fff !important;
  }
  .btn-return {
    background: #4fbc47 !important;
    color: #fff !important;
  }
  .dialog {
    background-color: #d7e3d7 !important;
    fieldset {
      background: #fff !important;
    }
    .product-variant {
      background-color: #d7e3d7 !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #d7e3d7 !important;
    }
    .products-search {
      background-color: #d7e3d7;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }

    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #e2ede2 !important;

      .v-sheet.v-alert .v-icon.v-icon {
        color: #8fc026 !important;
        caret-color: #8fc026 !important;
      }
    }
  }

  .v-application--is-ltr {
    fieldset {
      background: #e2ede2;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: none !important;
      }
    }
  }
  .v-application .v-application--is-ltr .v-text-field.v-input--is-focused .v-input__slot {
    border: 1px solid #fff !important;
  }
  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #e2ede2 !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #fff !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #e2ede2 !important;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #1a7b00 !important;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    color: black;
  }
}

.theme-green {
  .change-svg {
    path {
      fill: #000 !important;
    }
  }
  .v-application .v-application--is-ltr .v-text-field.v-input--is-focused .v-input__slot {
    &::after,
    &::before {
      display: none;
    }
  }
  .dialog {
    .v-sheet.v-alert {
      .mdi-information::before {
        color: #fff !important;
      }
    }
  }
  .second-tab .v-text-field.v-input--is-focused .v-label {
    color: #fff !important;
    opacity: 0.48;
  }
  .products-list .text-subtitle-1 {
    color: white;
  }
  .progress-linear {
    background-color: white !important;
    border-color: white !important;
  }
  .btn-anulat {
    background: #fff !important;
    color: #000;
  }
  .btn-return.v-btn--disabled {
    background: #51792e !important;
    color: #083924 !important;
  }
  .btn-return {
    background: #f7d523 !important;
    color: #000 !important;
  }
  .dialog {
    color: white !important;
    background-color: #0a5133 !important;
    fieldset {
      background: #fff !important;
    }
    .product-variant {
      background-color: #0a5133 !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #0a5133 !important;
    }
    .products-search {
      background-color: #0a5133;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }
    .v-image__image.v-image__image--cover {
      filter: invert(1);
    }
    .product-img .v-image__image.v-image__image--cover {
      filter: none;
    }
    .v-card,
    .v-input,
    .product-wrapper .product-variant .product-variant-details {
      color: white !important;
    }
    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #2f6742 !important;
      color: white !important;

      .v-application .info--text,
      .v-icon.v-icon {
        color: #fff !important;
        caret-color: #fff !important;
      }
    }
  }

  .v-label {
    /* color: #fff; */
  }
  .v-application--is-ltr {
    fieldset {
      background: #2f6742;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: none !important;
      }
    }
  }
  .v-application .v-application--is-ltr .v-text-field.v-input--is-focused .v-input__slot {
    border: 1px solid #dac525 !important;
  }

  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #2f6742 !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #0e5133 !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #2f6742 !important;
  }
  .container--fluid .v-subheader {
    color: #fff;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #f7d523 !important;
    color: #000;
  }

  .container--fluid .btn-change .v-icon--left::before {
    filter: brightness(0) saturate(100%) invert(49%) sepia(93%) saturate(604%) hue-rotate(41deg) brightness(95%)
      contrast(90%);
  }
  .v-input__append-inner .v-input__icon--append,
  .v-input--selection-controls__input .mdi.mdi-checkbox-marked {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
  .v-btn.v-btn--has-bg {
    background-color: #fff !important;
  }
  .container--fluid .v-btn__content {
    color: #000 !important;
  }
  .v-input--selection-controls__input {
    .mdi:before {
      border: 1px solid #e2e2e2;
    }
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #fff;
    & a {
      color: #fff;
    }
  }
}

.theme-light-blue {
  .dialog {
    .products-search-input .v-label {
      top: 9px;
      color: #000 !important;
    }
  }
  .btn-change.v-btn.v-btn--has-bg {
    background-color: #2a335b !important;
  }
  .container {
    .v-application--wrap {
      .v-input {
        .v-input__slot {
          border-width: none !important;
          border-style: none !important;
          border-color: none !important;
        }
      }
    }
  }
  .second-tab .v-text-field.v-input--is-focused .v-label {
    top: 0px !important;
    left: 0px !important;
  }
  .products-list .text-subtitle-1 {
    color: black;
  }
  .progress-linear {
    background-color: #39a0ff !important;
    border-color: #39a0ff !important;
  }
  .btn-anulat {
    background: #fff !important;
    color: #000;
  }
  .btn-return.v-btn--disabled {
    background: #b2d7f9 !important;
    color: #fff !important;
  }
  .btn-return {
    background: #39a0ff !important;
    color: #fff !important;
  }
  .dialog {
    background-color: #e6eff7 !important;
    fieldset {
      background: #fff !important;
    }
    .product-variant {
      background-color: #e6eff7 !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #e6eff7 !important;
    }
    .products-search {
      background-color: #e6eff7;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }

    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #f4f9fe !important;

      .v-sheet.v-alert .v-icon.v-icon {
        color: #39a0ff !important;
        caret-color: #39a0ff !important;
      }
      .mdi:before {
        color: black !important;
      }
    }
    .v-sheet.v-alert .mdi:before {
      color: #39a0ff !important;
    }
  }

  .v-label {
    color: #000;
  }
  .v-application--is-ltr {
    fieldset {
      background: #f4f9fe;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: none !important;
      }
    }
  }
  .v-application .v-application--is-ltr .v-text-field.v-input--is-focused .v-input__slot {
    border: 1px solid #fff !important;
  }
  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #f4f9fe !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #fff !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #f4f9fe !important;
  }
  .container--fluid .v-subheader {
    color: #000;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #39a0ff !important;
    color: #fff;
  }

  .v-btn--back.v-btn.v-btn--has-bg:not(.btn-return) {
    background-color: #fff !important;
  }
  /* .v-btn.v-btn--has-bg :not(.btn-return) {
    background-color: #2a335b !important;
  } */
  .container--fluid .v-btn__content {
    color: #fff !important;
  }
  .v-input--selection-controls__input {
    .mdi:before {
      border: 1px solid #676767;
    }
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #000;
    & a {
      color: #000;
    }
  }
}

.products-text {
  padding-bottom: 0 !important;
}

.theme-dark {
  .container {
    .v-application--wrap {
      .v-input.v-input--is-focused {
        .v-input__slot {
          border: 1px solid #d27f29 !important;
        }
      }
    }
  }
  .products-list .text-subtitle-1 {
    color: white;
  }
  .close-icon {
    cursor: pointer;
    path {
      fill: #fff;
    }
  }
  .dialog {
    .v-sheet.v-alert {
      .mdi-information::before {
        color: #f79123 !important;
      }
    }
  }
  .progress-linear {
    background-color: #f79123 !important;
    border-color: #f79123 !important;
  }
  .btn-anulat {
    background: #ff6d3e !important;
    color: #fff;
  }
  .btn-return.v-btn--disabled {
    background: #2e4e35 !important;
    color: #5f6683 !important;
  }
  .btn-return {
    background: #2ab500 !important;
    color: #fff !important;
  }
  .dialog {
    color: white !important;
    background-color: #221f2e !important;
    fieldset {
      background: #fff !important;
    }
    .product-variant {
      background-color: #221f2e !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #221f2e !important;
    }
    .products-search {
      background-color: #221f2e;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }
    .v-image__image.v-image__image--cover {
      filter: invert(1);
    }
    .product-img .v-image__image.v-image__image--cover {
      filter: none;
    }
    .v-card,
    .v-input,
    .product-wrapper .product-variant .product-variant-details {
      color: white !important;
    }
    .product-wrapper .v-icon.v-icon {
      color: #fff !important;
      caret-color: #fff !important;
    }
    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #2e2b3f !important;
      color: white !important;
    }

    .v-application .info--text,
    .v-sheet.v-alert .v-icon.v-icon {
      color: #f79123 !important;
      caret-color: #f79123 !important;
    }
  }

  .v-label {
    color: #828289;
  }
  .v-select__slot {
    .v-label {
      color: #fff;
    }
  }
  .second-tab .v-text-field.v-input--is-focused .v-label {
    color: #fff !important;
    opacity: 0.48;
  }
  .v-application--is-ltr {
    fieldset {
      background: #2e2b3f;
    }

    .v-text-field.v-input--is-focused {
      .v-input__slot {
        border-color: #d27f29 !important;
      }
      fieldset {
        border: none !important;
      }
    }
  }
  .second-tab .v-text-field .v-label {
    color: #fff !important;
    opacity: 0.48;
  }
  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #2e2b3f !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #4f4d5e !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #2e2b3f !important;
  }
  .container--fluid .v-subheader {
    color: #fff;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #2ab500 !important;
    color: #fff;
  }

  .v-btn--back.v-btn.v-btn--has-bg:not(.btn-return) {
    background-color: #f96c3f !important;
    .v-btn__content {
      color: #fff;
    }
  }
  .v-btn.v-btn--has-bg {
    // background-color: #4f4d5e !important;
  }
  .container--fluid .v-btn__content {
    color: #fff !important;
  }
  .v-input--selection-controls__input {
    .mdi:before {
      border: 1px solid #676767;
    }
  }

  .v-input__append-inner .v-input__icon--append,
  .v-input--selection-controls__input .mdi.mdi-checkbox-marked {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #fff;
    & a {
      color: #fff;
    }
  }
}

.v-radio .v-input--selection-controls__input .mdi:before {
  border: none !important;
  color: #000;
}

::-webkit-scrollbar-track {
  -webkit-box-shadow: none;
  background-color: #fff;
}

::-webkit-scrollbar {
  width: 8px;
  background-color: #f5f5f5;
}

::-webkit-scrollbar-thumb {
  border-radius: 10px;
  -webkit-box-shadow: none;
  background-color: #d8d8d8;
}

.theme-blue {
  .modal-expand-arrow {
    path {
      fill: #fff !important;
    }
  }
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #2a335b;
  }
  .close-icon {
    cursor: pointer;
    path {
      fill: #fff;
    }
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #2a335b;
  }
  .dialog {
    .v-sheet.v-alert {
      .mdi-information::before {
        color: #fff !important;
      }
    }
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #121935 !important;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #fff;
  }
}
.theme-light-green {
  .dialog {
    .products-search-input .v-label {
      top: 9px;
      color: #000 !important;
    }
  }
  .container {
    .v-application--wrap {
      .v-input {
        .v-input__slot {
          border-width: none !important;
          border-style: none !important;
          border-color: none !important;
        }
      }
    }
  }
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #e2ede2;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #e2ede2;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #8fc026;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #000;
  }
}
.theme-green {
  .modal-expand-arrow {
    path {
      fill: #fff !important;
    }
  }
  .container {
    .v-application--wrap {
      .v-input {
        .v-input__slot {
          border-width: none !important;
          border-style: none !important;
          border-color: none !important;
        }
      }
    }
  }
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #2f6742;
  }
  .close-icon {
    cursor: pointer;
    path {
      fill: #fff;
    }
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #2f6742;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #062c1b;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #fff;
  }
}
.theme-light-blue {
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #f4f9fe;
  }
  .dialog {
    .v-sheet.v-alert {
      .mdi-information::before {
        color: #39a0ff !important;
      }
    }
  }

  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #000;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #f4f9fe;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #39a0ff;
  }
}
.theme-dark {
  .modal-expand-arrow {
    path {
      fill: #fff !important;
    }
  }
  .container {
    .v-application--wrap {
      .v-input {
        .v-input__slot {
          border-width: none !important;
          border-style: none !important;
          border-color: none !important;
        }
      }
    }
  }
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #4f4d5e;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #4f4d5e;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #221f2ed2;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #fff;
  }
}
.form-types {
  .row {
    row-gap: 5px;
    .v-text-field.error--text {
      margin-bottom: 10px;
    }
  }
  .v-text-field.v-text-field--enclosed .v-text-field__details {
    padding: 0 12px !important;
    margin-bottom: -13px;
    bottom: 0 !important;
    left: -9px;
  }
  .v-select-wrap .v-text-field.v-text-field--enclosed .v-text-field__details {
    padding: 0 12px !important;
    margin-bottom: -17px;
    bottom: 0 !important;
    left: -9px;
  }
}
</style>
